import { useEffect, useRef, useState } from "react";
import {
  ConfigProvider,
  Button,
  Form,
  Input,
  Spin,
  Empty,
  Pagination,
  message,
  Divider,
} from "antd";
import type { PaginationProps } from "antd";
import { useSelector } from "react-redux";
import { useNavigate, useParams, useOutletContext } from "react-router-dom";
import { publicPath } from "@/utils/base";
import BindModal from "./components/BindModal";

import api from "@/api";
import apiAuth from "@/api/auth";
import "./index.less";

import dataJson from "@/../public/data.json";

const prefix = "productDevicePlugin";
function DevicePlugin() {
  const { product_detail, req_err, two_bars, app_list } = dataJson;
  const filterFormJson = product_detail.device_plugin.filter_form;

  const [addOpen, setAddOpen] = useState(false);
  const [pageData, setPageData] = useState<any[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState(false);

  const [filterForm] = Form.useForm();

  const { id } = useParams();
  const navigate = useNavigate();
  const { isTampered = false } = useOutletContext<{ isTampered?: boolean }>() || {};
  const productManageDetail = useSelector((state: any) => state.productManageDetail);

  // 分页
  const pageInit = {
    pageSize: 20,
    pageNo: 1,
  };
  const pageNo = useRef<number>(pageInit.pageNo);
  const pagesize = useRef<number>(pageInit.pageSize);
  const onFilter = () => {
    pageNo.current = pageInit.pageNo;
    pagesize.current = pageInit.pageSize;
    getPageData();
  };
  const onShowSizeChange: PaginationProps["onShowSizeChange"] = (current, pageSize) => {
    pagesize.current = pageSize;
  };
  const getPageData = async () => {
    try {
      setLoading(true);
      const filterFormValues = await filterForm.getFieldsValue();
      const res: any = await api.getDevicePluginPage({
        relationProductIds: [id],
        page: pageNo.current,
        limit: pagesize.current,
        keyword: filterFormValues.name,
        typeKeys: filterFormValues.type,
        showModes: filterFormValues.status,
        showCurrentVersion: false,
      });
      if (res.code === 200) {
        setPageData(res.data.datas);
        setTotal(Number(res?.data?.total));
        setLoading(false);
      } else {
        setLoading(false);
        message.error(res.msg || req_err);
      }
    } catch (errorInfo: any) {
      console.error(errorInfo);
      setPageData([]);
      setTotal(0);
      setLoading(false);
    }
  };

  const [devicePluginList, setDevicePluginList] = useState<any[]>([]);
  const [devicePluginLoading, setDevicePluginLoading] = useState(false);
  const getDevicePluginPage = async (keyword?: string) => {
    try {
      setDevicePluginLoading(true);
      const res: any = await api.getDevicePluginNoPage({
        page: 1,
        keyword,
        // showModes: ["PREPARING"],
        bindRelationProductIds: [id],
        showCurrentVersion: false,
      });
      if (res.code === 200) {
        setDevicePluginList(res.data);
        setDevicePluginLoading(false);
      } else {
        message.error(res.msg || req_err);
      }
    } catch {
      setDevicePluginLoading(false);
    }
  };
  const onFilterDevicePlugin = (e: any) => {
    getDevicePluginPage(e.target.value);
  };
  // 绑定
  const showAddModal = () => {
    setAddOpen(true);
  };

  // 获取鉴权token
  const iconTokenRef = useRef(null); // 存储鉴权token
  const queryIconToken = async () => {
    try {
      const res: any = await apiAuth.getSuperAuth({
        authSystem: "OPEN_PLATFORM",
      });
      if (res?.code == 200) {
        iconTokenRef.current = res?.data?.superAccessToken;
      } else {
        message.error(res.msg);
      }
    } catch (errorInfo: any) {
      console.error(errorInfo);
    }
  };
  const handleAddOpenOk = async (params: any) => {
    if (!params?.length) {
      return;
    }
    await queryIconToken();
    if (!iconTokenRef.current) {
      return;
    }
    const checkedIds = params.map((item: any) => item.id);
    apiAuth
      .bindProduct(iconTokenRef.current, {
        appIds: checkedIds,
        productId: id,
        productName: productManageDetail.name,
      })
      .then((res: any) => {
        if (res.code === 200) {
          message.success(product_detail.device_plugin.binding_success);
          setAddOpen(false);
          getPageData();
        } else {
          message.error(res.msg);
        }
      })
      .catch(() => {
        message.error(req_err);
      });
  };
  const handleAddCancel = () => {
    setAddOpen(false);
  };
  useEffect(() => {
    if (addOpen) {
      getDevicePluginPage();
    }
  }, [addOpen]);

  const clickCard = (item: any) => {
    navigate(`${publicPath}/my_device_plugin_detail/info/${item.id}`);
  };

  useEffect(() => {
    getPageData();
  }, [id]);

  const getImg = (item: any) => {
    if (item?.resources[0]?.url) {
      return item?.resources[0]?.url;
    } else {
      return app_list.init_img;
    }
  };

  return (
    <div className={prefix}>
      <div className={`${prefix}-inner`}>
        <div className={`${prefix}-header`}>
          <div className={`${prefix}-header-text`}>
            <div className={`${prefix}-header-title`}>{product_detail.device_plugin.detail_title_text}</div>
            <div className={`${prefix}-header-tip`}>{product_detail.device_plugin.info_tip}</div>
          </div>
        </div>
        <div className={`${prefix}-content`}>
          <div className={`${prefix}-top`}>
            <Form form={filterForm} layout="inline">
              <Form.Item
                name="name"
                getValueFromEvent={(e) => e.target.value.replace(/(^\s*)|(\s*$)/g, "")}
                style={{ marginRight: 56 }}
              >
                <Input placeholder={filterFormJson.name_placeholder} onPressEnter={onFilter} style={{ width: 296 }} />
              </Form.Item>
            </Form>
            <ConfigProvider
              theme={{
                components: {
                  Button: {
                    borderRadius: 0,
                    paddingInline: 40,
                    paddingBlock: 8,
                  },
                },
              }}
            >
              <Button type="primary" onClick={showAddModal} disabled={isTampered}>
                {product_detail.device_plugin.binding}
              </Button>
            </ConfigProvider>
          </div>
          <Divider />
          <Spin spinning={loading} size="large" wrapperClassName={`${prefix}-spin`}>
            {total === 0 ? (
              <Empty />
            ) : (
              <div className={`${prefix}-content`}>
                {pageData.map((item: any) => {
                  return (
                    <div
                      key={item.id}
                      onClick={() => {
                        clickCard(item);
                      }}
                      className={`${prefix}-content-item`}
                    >
                      <div
                        className={`${prefix}-content-status`}
                        style={{ color: item?.textColor, backgroundColor: item?.bgColor }}
                      >
                        {item.title}
                      </div>
                      <div className={`${prefix}-content-inner`}>
                        <img src={getImg(item)} alt="" />
                        <div>
                          <div title={item?.name} className={`${prefix}-content-title one-line-ellipsis`}>
                            {item?.name || two_bars}
                          </div>
                          <div>{item?.deviceType?.text}</div>
                          <div className="one-line-ellipsis">
                            {product_detail.device_plugin.package_version}
                            {item.version}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Spin>
          <Pagination
            pageSize={pagesize.current}
            current={pageNo.current}
            total={total}
            showSizeChanger
            showQuickJumper
            showTotal={(total) => `共 ${total} 条`}
            align="end"
            className={`${prefix}_page`}
            onShowSizeChange={onShowSizeChange}
            onChange={(page: number) => {
              pageNo.current = page;
              getPageData();
            }}
          />
        </div>
      </div>
      <BindModal
        addOpen={addOpen}
        devicePluginLoading={devicePluginLoading}
        // value={bindValue}
        data={devicePluginList}
        handleSeach={onFilterDevicePlugin}
        handleAddOpenOk={handleAddOpenOk}
        handleAddCancel={handleAddCancel}
      />
    </div>
  );
}

export default DevicePlugin;
